import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import SitesPage from './pages/SitesPage';
import SystemPage from './pages/SystemPage';
import CheckoutsPage from './pages/CheckoutsPage';
import CheckoutTemplatesPage from './pages/CheckoutTemplatesPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import CamerasPage from './pages/CamerasPage';
import DocksPage from './pages/DocksPage';
import ProfilesPage from './pages/ProfilesPage';
import NetworkPage from './pages/NetworkPage';
import UpdatesPage from './pages/UpdatesPage';
import TalkGroupsPage from './pages/TalkGroupsPage';
import TeamsPage from './pages/TeamsPage';
import {
  LicensingPage,
  HomePage,
} from './pages/stubs';
import DSMPage from './pages/dsm/DSMPage';
import PairingPage from './pages/checkout/PairingPage';
import CheckoutSuccessPage from './pages/checkout/SuccessPage';
import CameraCheckoutPage from './pages/checkout/CameraCheckoutPage';
import NeedCameraPage from './pages/checkout/NeedCameraPage';
import PickCameraPage from './pages/checkout/PickCameraPage';
import CheckoutStatusHeartbeat from './components/checkouts/CheckoutStatusHeartbeat';
import DevLoginPage from './pages/DevLoginPage';
import { TenantProvider } from './context/TenantContext';
import { useAuth } from './context/AuthContext';
import { useToast } from './components/Toast';
import useTokenGuard from './hooks/useTokenGuard';

function FullScreenMessage({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#8b949e' }}>
      {children}
    </div>
  );
}

function App() {
  const { ToastContainer } = useToast();
  // Alta token check. Inert unless served from *.alta.avigilon.com, so local
  // dev-login is unaffected — see hooks/useTokenGuard.js.
  const { isChecking, isRedirecting } = useTokenGuard();
  const { isAuthenticated, devLoginEnabled, loading } = useAuth();

  // Hold the UI while the access token is being verified, and while the browser
  // is on its way to the sign-in page, so signed-out users never see portal
  // chrome flash first.
  if (isChecking || isRedirecting) {
    return (
      <FullScreenMessage>
        {isRedirecting ? 'Redirecting to sign in...' : 'Verifying session...'}
      </FullScreenMessage>
    );
  }

  // While loading auth state, show nothing (or a spinner)
  if (loading) {
    return <FullScreenMessage>Loading...</FullScreenMessage>;
  }

  // If dev-login is enabled and user is not authenticated, show login page
  if (devLoginEnabled && !isAuthenticated) {
    return <DevLoginPage />;
  }

  return (
    <TenantProvider>
      <ToastContainer />
      <CheckoutStatusHeartbeat />
      <Routes>
        {/* Full-bleed kiosk routes — no sidebar/header. Reached from the
            Checkouts list page (see pages/CheckoutsPage.jsx → handleLaunch). */}
        <Route path="/checkout/camera" element={<CameraCheckoutPage />} />
        <Route path="/checkout/need-camera" element={<NeedCameraPage />} />
        <Route path="/checkout/pick-camera" element={<PickCameraPage />} />
        <Route path="/checkout/pair" element={<PairingPage />} />
        <Route path="/checkout/pair/success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

        {/* DSM (Device State Monitor) — full-bleed dashboard, opens in a new
            tab from the sidebar's DSM tile. Owns its own topbar/sidebar/drawer. */}
        <Route path="/dsm" element={<DSMPage />} />

        {/* Everything else renders inside the portal chrome. */}
        <Route
          path="*"
          element={
            <MainLayout>
              <Routes>
                <Route path="/" element={<Navigate to="/sites" replace />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/sites" element={<SitesPage />} />
                <Route path="/checkout" element={<Navigate to="/checkout/checkouts" replace />} />
                <Route path="/checkout/checkouts" element={<CheckoutsPage />} />
                <Route path="/checkout/templates" element={<CheckoutTemplatesPage />} />
                <Route path="/devices" element={<Navigate to="/devices/cameras" replace />} />
                <Route path="/devices/cameras" element={<CamerasPage />} />
                <Route path="/devices/docks" element={<DocksPage />} />
                <Route path="/devices/profiles" element={<ProfilesPage />} />
                <Route path="/devices/updates" element={<UpdatesPage />} />
                <Route path="/devices/networks" element={<NetworkPage />} />
                <Route path="/users" element={<Navigate to="/users/overview" replace />} />
                <Route path="/users/overview" element={<UsersPage />} />
                <Route path="/users/roles" element={<RolesPage />} />
                <Route path="/comms" element={<Navigate to="/comms/talk-groups" replace />} />
                <Route path="/comms/talk-groups" element={<TalkGroupsPage />} />
                <Route path="/comms/teams" element={<TeamsPage />} />
                <Route path="/talk-groups" element={<Navigate to="/comms/talk-groups" replace />} />
                <Route path="/licensing" element={<LicensingPage />} />
                <Route path="/network" element={<NetworkPage />} />
                <Route path="/system" element={<SystemPage />} />
                <Route path="*" element={<Navigate to="/sites" replace />} />
              </Routes>
            </MainLayout>
          }
        />
      </Routes>
    </TenantProvider>
  );
}

export default App;
